import type { FC } from 'react';
import React, { useState } from 'react';
import {
  Flex,
  Box,
  Glyph,
  Heading,
  Popover,
  PopoverContainer,
  Body,
} from '@edreamsodigeo/prisma-design-system';
import { createFeature, useTranslation } from '@edreamsodigeo/frontend-core';

import { AIRCRAFT_FILTER_COMPONENT } from '@/versionedComponents';
import type { TransportType } from '@/generated/graphql';
import type { VehicleModelProps } from './types';

const isTrain = (transportType: TransportType) => transportType === 'TRAIN';

export const TransportTypeIcon: FC<{ transportType: TransportType }> = ({
  transportType,
}) => (
  <Glyph
    category={isTrain(transportType) ? 'TravelProducts' : 'Planes'}
    color="neutral.80"
    name={isTrain(transportType) ? 'Train' : 'FlightRight'}
    size="small"
  />
);

export const AircraftTooltip = ({ label }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <PopoverContainer>
      <Box
        data-testid="aircraft-info-button"
        sx={{ marginTop: '5px' }}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        <Glyph
          category="Alerts"
          name="InfoCircle"
          size="small"
          color="neutral.80"
        />
        <Popover
          open={isOpen}
          placement="bottom"
          align="end"
          padding="normal"
          size="small"
        >
          <Body type="xSmall">{label}</Body>
        </Popover>
      </Box>
    </PopoverContainer>
  );
};

export const AircraftTooltipComponent = createFeature({
  name: AIRCRAFT_FILTER_COMPONENT,
  variations: [null, AircraftTooltip],
  version: 1,
});

export const VehicleModel: FC<VehicleModelProps> = ({
  model,
  transportType,
}) => {
  const { t } = useTranslation();

  const description = isTrain(transportType) ? 'TRAIN' : model;
  if (!description) return null;

  return (
    <Flex
      alignItems="center"
      gap="2x_small"
      sx={{ height: '24px' }}
      data-testid="aircraft-info-pill"
    >
      <TransportTypeIcon transportType={transportType} />
      <Heading type="5" color="neutral.80">
        {description}
      </Heading>
      {!isTrain(transportType) && (
        <AircraftTooltipComponent label={t('filters.aircraft.tooltip')} />
      )}
    </Flex>
  );
};
